<template>
  <div class="space-y-3">
    <h1 class="text-base font-semibold text-text lg:sr-only">Naming Series</h1>

    <!-- Table -->
    <div class="bg-background-card rounded-lg shadow-card overflow-hidden">
      <div class="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <span class="text-xs text-text-muted">
          Document numbers use tokens such as
          <code class="text-xs bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 px-1 py-0.5 rounded font-mono">{YYYY}</code>,
          <code class="text-xs bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 px-1 py-0.5 rounded font-mono">{MM}</code>, and
          <code class="text-xs bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 px-1 py-0.5 rounded font-mono">####</code>.
        </span>
        <Button
          label="New Series"
          icon="fa-solid fa-plus"
          size="small"
          class="ml-auto"
          @click="openCreate"
        />
      </div>
      <DataTable
        :value="seriesList"
        :loading="isLoading"
        data-key="id"
        striped-rows
        class="p-datatable-sm"
      >
        <Column field="entityType" header="Entity Type" style="width: 180px">
          <template #body="{ data }">
            <span class="text-sm">{{ entityTypeLabel(data.entityType) }}</span>
          </template>
        </Column>
        <Column field="name" header="Name">
          <template #body="{ data }">
            <span class="text-sm">{{ data.name }}</span>
            <span v-if="data.isSystemReserved" class="ml-2 text-xs px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 text-surface-600 dark:text-surface-300 font-medium">System</span>
          </template>
        </Column>
        <Column field="format" header="Format">
          <template #body="{ data }">
            <code class="text-xs bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 px-1 py-0.5 rounded font-mono">{{ data.format }}</code>
          </template>
        </Column>
        <Column header="Default" style="width: 90px">
          <template #body="{ data }">
            <i v-if="data.isDefault" class="fa-solid fa-check text-green-600 dark:text-green-400" />
          </template>
        </Column>
        <Column header="Actions" style="width: 120px">
          <template #body="{ data }">
            <div class="flex gap-1">
              <Button
                v-if="!data.isSystemReserved"
                icon="fa-solid fa-pen"
                severity="info"
                size="small"
                text
                rounded
                v-tooltip.top="'Edit'"
                @click="openEdit(data)"
              />
              <Button
                icon="fa-solid fa-sliders"
                severity="secondary"
                size="small"
                text
                rounded
                v-tooltip.top="'View / Adjust Counters'"
                @click="openCounters(data)"
              />
              <Button
                v-if="!data.isSystemReserved"
                icon="fa-solid fa-trash"
                severity="danger"
                size="small"
                text
                rounded
                v-tooltip.top="'Delete'"
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="text-center py-12">
            <i class="fa-solid fa-hashtag text-5xl text-surface-400 dark:text-surface-500 mb-3" />
            <p class="text-lg font-semibold text-text-muted mb-2">No naming series configured</p>
            <p class="text-sm text-text-muted mb-4">Create a series to auto-generate document numbers.</p>
            <Button label="New Series" icon="fa-solid fa-plus" @click="openCreate" />
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Counters panel (shown below table when a series is selected) -->
    <div v-if="selectedSeriesDetail" class="bg-background-card rounded-lg shadow-card p-6 space-y-3">
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-sm">
          Counters for <span class="text-primary">{{ selectedSeriesDetail.name }}</span>
        </h2>
        <Button
          icon="fa-solid fa-xmark"
          severity="secondary"
          text
          rounded
          size="small"
          @click="selectedSeriesDetail = null"
        />
      </div>
      <DataTable :value="selectedSeriesDetail.counters" data-key="prefix" class="p-datatable-sm">
        <Column field="prefix" header="Resolved Prefix">
          <template #body="{ data }">
            <span v-if="data.prefix">{{ data.prefix }}</span>
            <span v-else class="text-text-muted italic">(no prefix)</span>
          </template>
        </Column>
        <Column field="current" header="Current Counter" style="width: 150px" />
        <Column field="nextNumber" header="Next Number" style="width: 130px" />
        <Column header="Adjust" style="width: 100px">
          <template #body="{ data }">
            <Button
              icon="fa-solid fa-pen-to-square"
              severity="secondary"
              text
              rounded
              size="small"
              @click="openAdjustCounter(data.prefix, data.current)"
            />
          </template>
        </Column>
        <template #empty>
          <span class="text-sm text-text-muted">No numbers generated yet for this series.</span>
        </template>
      </DataTable>
    </div>
  </div>

  <!-- Form dialog -->
  <NamingSeriesFormDialog
    v-model:visible="showFormDialog"
    :series="editingSeries"
    @save="handleFormSave"
  />

  <!-- Adjust counter dialog -->
  <AdjustCounterDialog
    v-model:visible="showAdjustDialog"
    :prefix="adjustPrefix"
    :current-value="adjustCurrentValue"
    @save="handleAdjustSave"
  />

</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import type { MaintenanceNamingSeriesSummaryDto, CreateNamingSeriesCommand, UpdateNamingSeriesCommand } from '@/shared/services/maintenanceNamingSeries';
import { NamingSeriesService } from '@/shared/services/maintenanceNamingSeries';
import { useNamingSeriesManagement } from '@/features/maintenance/composables/useNamingSeriesManagement';

definePage({ meta: { requiresProject: true, title: 'Naming Series' } });

const confirm = useConfirm();
const { seriesList, isLoading, load, create, update, deleteSeries, adjustCounter } = useNamingSeriesManagement();

const showFormDialog = ref(false);
const editingSeries = ref<MaintenanceNamingSeriesSummaryDto | null>(null);
const selectedSeriesDetail = ref<import('@/shared/services/maintenanceNamingSeries').MaintenanceNamingSeriesDetailDto | null>(null);
const showAdjustDialog = ref(false);
const adjustPrefix = ref('');
const adjustCurrentValue = ref(0);

onMounted(() => load());

function entityTypeLabel(type: string): string {
  const map: Record<string, string> = {
    TaskList: 'Task List',
    MaintenancePlan: 'Maintenance Plan',
    MaintenanceItem: 'Maintenance Item',
    MaintenanceAnalysis: 'Maintenance Analysis',
  };
  return map[type] ?? type;
}

function openCreate(): void {
  editingSeries.value = null;
  showFormDialog.value = true;
}

function openEdit(series: MaintenanceNamingSeriesSummaryDto): void {
  editingSeries.value = series;
  showFormDialog.value = true;
}

async function openCounters(series: MaintenanceNamingSeriesSummaryDto): Promise<void> {
  try {
    selectedSeriesDetail.value = await NamingSeriesService.getById(series.id);
  } catch {
    selectedSeriesDetail.value = null;
  }
}

function openAdjustCounter(prefix: string, current: number): void {
  adjustPrefix.value = prefix;
  adjustCurrentValue.value = current;
  showAdjustDialog.value = true;
}

async function handleAdjustSave(prefix: string, newValue: number): Promise<void> {
  const ok = await adjustCounter(prefix, newValue);
  if (ok && selectedSeriesDetail.value) {
    await openCounters(seriesList.value.find((s) => s.id === selectedSeriesDetail.value!.id)!);
  }
}

function confirmDelete(series: MaintenanceNamingSeriesSummaryDto): void {
  confirm.require({
    message: `Delete naming series "${series.name}"? This cannot be undone.`,
    header: 'Delete Series',
    icon: 'fa-solid fa-triangle-exclamation',
    acceptClass: 'p-button-danger',
    accept: () => deleteSeries(series.id, series.name),
  });
}

async function handleFormSave(
  command: CreateNamingSeriesCommand | (UpdateNamingSeriesCommand & { id: string }),
): Promise<void> {
  if ('id' in command) {
    await update(command.id, { name: command.name, format: command.format, isDefault: command.isDefault });
  } else {
    await create(command);
  }
}
</script>
